const UUID_PATH_SEGMENT = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
const codeDetailPattern = new RegExp(`^/app/codes/(${UUID_PATH_SEGMENT})(?:/(content|design))?$`, 'u');
const formDetailPattern = new RegExp(`^/app/forms/(${UUID_PATH_SEGMENT})(?:/(build))?$`, 'u');
const submissionDetailPattern = new RegExp(`^/app/data/submissions/(${UUID_PATH_SEGMENT})$`, 'u');

export type WorkspaceRoute =
  | { kind: 'home'; path: '/app' }
  | { kind: 'code-list'; path: '/app/codes' }
  | { kind: 'code-create'; path: '/app/codes/new' }
  | { kind: 'code-detail'; path: string; resourceId: string; surface: 'content' | 'design' }
  | { kind: 'form-list'; path: '/app/forms' }
  | { kind: 'form-create'; path: '/app/forms/new' }
  | { kind: 'form-detail'; path: string; resourceId: string; surface: 'build' }
  | { kind: 'data'; path: string; page: 'legacy' | 'submissions' | 'submission-detail' | 'incidents' | 'by-qr'; submissionId: string | null }
  | { kind: 'settings'; path: '/app/settings' }
  | { kind: 'billing'; path: '/app/settings/billing' }
  | { kind: 'notifications'; path: '/app/settings/notifications' }
  | { kind: 'not-found'; path: string };

export function normalizeWorkspacePath(path: string): string {
  if (path === '/app/' || path === '/app') return '/app';
  return path.endsWith('/') ? path.slice(0, -1) : path;
}

export function resolveWorkspaceRoute(path: string): WorkspaceRoute {
  const normalized = normalizeWorkspacePath(path);
  if (normalized === '/app') return { kind: 'home', path: '/app' };
  if (normalized === '/app/codes') return { kind: 'code-list', path: '/app/codes' };
  if (normalized === '/app/codes/new') return { kind: 'code-create', path: '/app/codes/new' };
  const codeDetail = codeDetailPattern.exec(normalized);
  if (codeDetail?.[1]) return { kind: 'code-detail', path: normalized, resourceId: codeDetail[1], surface: codeDetail[2] === 'design' ? 'design' : 'content' };
  if (normalized === '/app/forms') return { kind: 'form-list', path: '/app/forms' };
  if (normalized === '/app/forms/new') return { kind: 'form-create', path: '/app/forms/new' };
  const formDetail = formDetailPattern.exec(normalized);
  if (formDetail?.[1]) return { kind: 'form-detail', path: normalized, resourceId: formDetail[1], surface: 'build' };
  if (normalized === '/app/data') return { kind: 'data', path: normalized, page: 'legacy', submissionId: null };
  if (normalized === '/app/data/submissions') return { kind: 'data', path: normalized, page: 'submissions', submissionId: null };
  const submissionDetail = submissionDetailPattern.exec(normalized);
  if (submissionDetail?.[1]) return { kind: 'data', path: normalized, page: 'submission-detail', submissionId: submissionDetail[1] };
  if (normalized === '/app/data/incidents') return { kind: 'data', path: normalized, page: 'incidents', submissionId: null };
  if (normalized === '/app/data/by-qr') return { kind: 'data', path: normalized, page: 'by-qr', submissionId: null };
  if (normalized === '/app/settings') return { kind: 'settings', path: '/app/settings' };
  if (normalized === '/app/settings/billing') return { kind: 'billing', path: '/app/settings/billing' };
  if (normalized === '/app/settings/notifications') return { kind: 'notifications', path: '/app/settings/notifications' };
  return { kind: 'not-found', path: normalized };
}

export function isWorkspaceSectionActive(route: WorkspaceRoute, section: 'codes' | 'forms' | 'data' | 'settings'): boolean {
  if (section === 'codes') return route.kind === 'code-list' || route.kind === 'code-create' || route.kind === 'code-detail';
  if (section === 'forms') return route.kind === 'form-list' || route.kind === 'form-create' || route.kind === 'form-detail';
  if (section === 'data') return route.kind === 'data';
  return route.kind === 'settings' || route.kind === 'billing' || route.kind === 'notifications';
}
